import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ShoppingBag, 
  Users, 
  DollarSign, 
  Package, 
  Search, 
  Filter, 
  ChevronDown, 
  ChevronUp, 
  Calendar, 
  Clock, 
  Eye, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Sparkles 
} from "lucide-react";
import { ORDER_API } from "../../data/api";

export const OrdersPanelContent = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const ordersPerPage = 12;

  const loadOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(ORDER_API.GET_ALL);
      setOrders(res.data.data || res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const toggleExpand = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return {
          bg: "bg-green-100",
          text: "text-green-700",
          icon: <CheckCircle size={16} />,
          gradient: "from-green-500 to-emerald-500",
        };
      case "Pending":
        return {
          bg: "bg-yellow-100",
          text: "text-yellow-700",
          icon: <Clock size={16} />,
          gradient: "from-yellow-500 to-amber-500",
        };
      case "Cancelled":
        return {
          bg: "bg-red-100",
          text: "text-red-700",
          icon: <XCircle size={16} />,
          gradient: "from-red-500 to-rose-500",
        };
      case "Processing":
        return {
          bg: "bg-blue-100",
          text: "text-blue-700",
          icon: <Package size={16} />,
          gradient: "from-blue-500 to-cyan-500",
        };
      default:
        return {
          bg: "bg-gray-100",
          text: "text-gray-700",
          icon: <AlertCircle size={16} />,
          gradient: "from-gray-500 to-gray-600",
        };
    }
  };

  // Filter orders
  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      order.orderId?.toString().includes(searchTerm) ||
      order.userId?.toString().includes(searchTerm);
    const matchesStatus =
      statusFilter === "all" || order.orderStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);

  // Stats
  const stats = {
    total: orders.length,
    completed: orders.filter((o) => o.orderStatus === "Completed").length,
    pending: orders.filter((o) => o.orderStatus === "Pending").length,
    revenue: orders
      .filter((o) => o.paymentStatus === "Completed")
      .reduce((sum, o) => sum + (o.finalAmount || 0), 0),
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1 }}
          className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center">
              <ShoppingBag className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-800 tracking-tight">
                Orders Dashboard
              </h2>
              <p className="text-gray-500">Track, review & manage all customer orders</p>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            {[
              {
                label: "Total Orders",
                value: stats.total,
                icon: ShoppingBag,
                gradient: "from-blue-500 to-cyan-500",
              },
              {
                label: "Completed",
                value: stats.completed,
                icon: CheckCircle,
                gradient: "from-green-500 to-emerald-500",
              },
              {
                label: "Pending",
                value: stats.pending,
                icon: Clock,
                gradient: "from-yellow-500 to-amber-500",
              },
              {
                label: "Total Revenue",
                value: `₹${stats.revenue.toLocaleString()}`,
                icon: DollarSign,
                gradient: "from-purple-500 to-pink-500",
              },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-4 shadow-lg border border-gray-100"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">{stat.label}</p>
                    <p className="text-2xl font-bold text-gray-800 mt-1">{stat.value}</p>
                  </div>
                  <div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center`}
                  >
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mt-6">
            {/* Search */}
            <div className="relative flex-1">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Search by Order ID or User ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <Filter
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                size={20}
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="pl-12 pr-8 py-3 bg-white border border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all appearance-none cursor-pointer"
              >
                <option value="all">All Status</option>
                <option value="Completed">Completed</option>
                <option value="Pending">Pending</option>
                <option value="Cancelled">Cancelled</option>
                <option value="Processing">Processing</option>
              </select>
            </div>
          </div>
        </motion.div>

        {/* Orders Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {currentOrders.map((order, index) => {
              const statusStyle = getStatusColor(order.orderStatus);
              return (
                <motion.div
                  key={order.orderId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow"
                >
                  {/* Card Header */}
                  <div className="p-5 border-b border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-xs text-gray-500">Order ID</p>
                        <p className="font-bold text-gray-800 text-lg">
                          #{order.orderId}
                        </p>
                      </div>
                      <span
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1 ${statusStyle.bg} ${statusStyle.text}`}
                      >
                        {statusStyle.icon}
                        {order.orderStatus}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Users size={14} />
                      <span>User: {order.userId}</span>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="p-5 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 flex items-center gap-2">
                        <Package size={16} />
                        Items
                      </span>
                      <span className="font-semibold text-orange-600">
                        {order.items?.length || 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 flex items-center gap-2">
                        <DollarSign size={16} />
                        Total Amount
                      </span>
                      <span className="font-semibold text-indigo-600">
                        ₹{order.totalAmount?.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 flex items-center gap-2">
                        <Sparkles size={16} />
                        Final Amount
                      </span>
                      <span className="font-semibold text-green-600">
                        ₹{order.finalAmount?.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  {/* Expand Button */}
                  <button
                    onClick={() => toggleExpand(order.orderId)}
                    className="w-full p-3 bg-gray-50 border-t border-gray-100 flex items-center justify-center gap-2 text-gray-600 hover:bg-gray-100 transition-colors"
                  >
                    {expandedOrder === order.orderId ? (
                      <>
                        <span>Show Less</span>
                        <ChevronUp size={16} />
                      </>
                    ) : (
                      <>
                        <span>View Details</span>
                        <ChevronDown size={16} />
                      </>
                    )}
                  </button>

                  {/* Expand Section */}
                  <AnimatePresence>
                    {expandedOrder === order.orderId && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="bg-gray-50 border-t border-gray-100"
                      >
                        <div className="p-5">
                          <p className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                            <Eye size={16} />
                            Order Items
                          </p>

                          {order.items && order.items.length > 0 ? (
                            <ul className="space-y-2">
                              {order.items.map((item, idx) => (
                                <li
                                  key={idx}
                                  className="flex justify-between items-center bg-white p-3 rounded-xl border border-gray-200"
                                >
                                  <div>
                                    <p className="font-medium text-gray-800">
                                      {item.productName || `Product #${item.productId}`}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      Qty: {item.quantity}
                                    </p>
                                  </div>
                                  <p className="font-semibold text-indigo-600">
                                    ₹{item.price?.toLocaleString()}
                                  </p>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-gray-500 text-center py-4">
                              No items in this order.
                            </p>
                          )}

                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Calendar size={14} />
                              <span>
                                Placed:{" "}
                                {order.createdAt
                                  ? new Date(order.createdAt).toLocaleString()
                                  : "N/A"}
                              </span>
                            </div>
                            {order.updatedAt && (
                              <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
                                <Clock size={14} />
                                <span>
                                  Updated: {new Date(order.updatedAt).toLocaleString()}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Empty State */}
        {filteredOrders.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <ShoppingBag size={40} className="text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No Orders Found</h3>
            <p className="text-gray-500">
              {searchTerm || statusFilter !== "all"
                ? "Try adjusting your search or filter"
                : "Orders will appear here once placed"}
            </p>
          </motion.div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-center mt-10 gap-2"
          >
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((number) => (
              <button
                key={number}
                onClick={() => setCurrentPage(number)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  currentPage === number
                    ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg"
                    : "bg-white border text-gray-700 hover:bg-gray-100"
                }`}
              >
                {number}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default OrdersPanelContent;
